import React from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import { ROUTE_MAP, PRODUCTS, renderProductIcon } from "../../constants/productConstants";

const NoProductSubscriptionCard = ({ slug, label, variant = "dashboard" }) => {
  const navigate = useNavigate();
  const route = ROUTE_MAP[slug];
  const displayLabel = PRODUCTS[slug]?.title || label;

  const ProductIcon = renderProductIcon(slug);

  if (variant === "dashboard") {
    return (
      <DashCard>
        <MainRow>
          <PlanTitle>
            <DashIconWrap>{ProductIcon}</DashIconWrap>
            <PlanTitleText>{displayLabel}</PlanTitleText>
          </PlanTitle>
        </MainRow>
        <BottomRow>
          <Message>
            There is no active subscription for this product.{" "}
            <CtaLink onClick={() => navigate(route)}>
              To subscribe click here.
            </CtaLink>
          </Message>
        </BottomRow>
      </DashCard>
    );
  }

  return (
    <SubCard onClick={() => navigate(route)}>
      <TopBar />
      <CardInner>
        <HeaderRow>
          <IconWrap>{ProductIcon}</IconWrap>
          <NotActivePill>Not Active</NotActivePill>
        </HeaderRow>

        <TitleBlock>
          <ProductName>{displayLabel}</ProductName>
          <Tagline>Unlock {displayLabel} and start using it today</Tagline>
        </TitleBlock>

        <CtaButton>View Plans</CtaButton>
      </CardInner>
    </SubCard>
  );
};

export default NoProductSubscriptionCard;

const DashCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  background: #ffffff;
  border: 1px dashed #d0d5dd;
  border-radius: 16px;
  box-shadow:
    0 1px 2px rgba(10, 13, 18, 0.04),
    0 4px 12px rgba(10, 13, 18, 0.06);
  padding: 16px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: #e4e7ec;
    border-radius: 16px 16px 0 0;
  }
`;

const MainRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const PlanTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PlanTitleText = styled.span`
  color: #181d27;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.2;
`;

const BottomRow = styled.div`
  display: flex;
  align-items: center;
  padding-top: 8px;
  border-top: 1px solid #e4e7ec;
  margin-top: 2px;
`;

const Message = styled.p`
  color: #667085;
  font-size: 12px;
  line-height: 1.6;
  margin: 0;
`;

const CtaLink = styled.span`
  color: #7f56d9;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover { color: #6941c6; }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const SubCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #ffffff;
  border-radius: 16px;
  box-shadow:
    0 1px 3px rgba(10, 13, 18, 0.06),
    0 4px 16px rgba(10, 13, 18, 0.06);
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.18s ease;

  &:hover {
    box-shadow:
      0 4px 12px rgba(10, 13, 18, 0.08),
      0 12px 32px rgba(127, 86, 217, 0.14);
    transform: translateY(-2px);
  }
`;

const TopBar = styled.div`
  height: 5px;
  background: linear-gradient(90deg, #e4e7ec 0%, #d6bbfb 50%, #e4e7ec 100%);
  background-size: 200% auto;
  animation: ${shimmer} 3s linear infinite;
  flex-shrink: 0;
`;

const CardInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px 24px 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const IconWrap = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #f4ebff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7f56d9;
  opacity: 0.5;

  svg {
    width: 22px;
    height: 22px;
  }
`;

const DashIconWrap = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #f4ebff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  opacity: 0.6;
  svg {
    width: 14px;
    height: 14px;
  }
`;

const NotActivePill = styled.span`
  display: inline-flex;
  align-items: center;
  background: #f2f4f7;
  color: #667085;
  border: 1px solid #e4e7ec;
  border-radius: 9999px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.03em;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const ProductName = styled.span`
  color: #101828;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const Tagline = styled.span`
  color: #667085;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.5;
`;

const CtaButton = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  background: linear-gradient(135deg, #7f56d9 0%, #9b8afb 100%);
  color: #ffffff;
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  transition: opacity 0.15s ease;
  box-shadow: 0 2px 8px rgba(127, 86, 217, 0.25);
  margin-top: auto;

  ${SubCard}:hover & {
    opacity: 0.9;
  }
`;